import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faCaretDown, faCaretUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import {
    archiveCustomField,
    deleteCustomField,
    reorderCustomField,
    unarchiveCustomField,
} from 'api/campaigns';
import arrayMove from 'array-move';
import { CampaignToolsEditCustomField } from 'assets/js/pages/campaign-settings/campaign-tools/CampaignToolsEditCustomField';
import * as DropdownMenu from 'components/dropdown-menu';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CustomField } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface NewViewCustomFieldFieldProps {
    field: CustomField;
    fieldOrder: number[];
    campaignId: number;
}

interface MutationArgs {
    campaignId: number;
    id: number;
}

interface ReorderArgs {
    campaignId: number;
    order: string;
}

const CampaignToolsViewCustomField = ({
    field,
    fieldOrder,
    campaignId,
}: NewViewCustomFieldFieldProps) => {
    const queryClient = useQueryClient();
    const { success, error } = useToast();

    const { mutate: archiveField, isPending: isArchiving } = useMutation({
        mutationFn: ({ campaignId, id }: MutationArgs) => archiveCustomField(campaignId, id),
        onSuccess: () => {
            success('The custom field has been archived');
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.customFields(campaignId) });
        },
        onError: (e: GivebutterAxiosError) => {
            error(
                e.response?.data.message ??
                    'Something went wrong while archiving. Please try again.',
            );
        },
    });

    const { mutate: unarchiveField, isPending: isUnArchiving } = useMutation({
        mutationFn: ({ campaignId, id }: MutationArgs) => unarchiveCustomField(campaignId, id),
        onSuccess: () => {
            success('The custom field has been unarchived.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.customFields(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(
                e.response?.data.message ??
                    'Something went wrong while unarchiving. Please try again.',
            );
        },
    });

    const { mutate: deleteField, isPending: isDeleting } = useMutation({
        mutationFn: ({ campaignId, id }: MutationArgs) => deleteCustomField(campaignId, id),
        onSuccess: () => {
            success('The custom field has been deleted.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.customFields(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Something went while deleting. Please try again.');
        },
    });

    const { mutate: reorderField } = useMutation({
        mutationFn: ({ campaignId, order }: ReorderArgs) => reorderCustomField(campaignId, order),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.customFields(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(
                e.response?.data.message ??
                    'Something went wrong while re-ordering. Please try again.',
            );
        },
    });

    const onClickMove = (e: React.MouseEvent<Element>, type: 'up' | 'down'): void => {
        e.preventDefault();

        const index = fieldOrder.findIndex(value => value === field.id);
        const newIndex = type === 'up' ? index - 1 : index + 1;
        const newOrder = arrayMove(fieldOrder, index, newIndex).toString();

        reorderField({ campaignId, order: newOrder });
    };

    return (
        <Box key={field.id} className="w-full p-4 lg:p-8">
            <Collapsible.Root className="flex flex-col gap-8">
                <div className="flex items-center gap-4 overflow-x-auto">
                    <div className="w-4 cursor-pointer">
                        <a href="#!" className="text-gray-300" onClick={e => onClickMove(e, 'up')}>
                            <FontAwesomeIcon icon={faCaretUp} />
                        </a>

                        <a
                            href="#!"
                            className="text-gray-300"
                            onClick={e => onClickMove(e, 'down')}
                        >
                            <FontAwesomeIcon icon={faCaretDown} />
                        </a>
                    </div>

                    <div className="flex w-full items-center gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-gray-200">
                            <IconType field={field} />
                        </div>
                        <div className="flex flex-col">
                            <h2 className="text-base font-medium">{field.title}</h2>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <BadgeType field={field} />
                        <Collapsible.Trigger asChild={true}>
                            <Button size="sm" variant="outline" color="neutral">
                                Edit
                                <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </Collapsible.Trigger>
                        <DropdownMenu.Root>
                            <DropdownMenu.EllipsisTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <AlertDialog.Root type="warning">
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            {field.archived_at ? 'Restore' : 'Archive'}
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>Archive Field</AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You are about to{' '}
                                                    {field.archived_at ? 'restore' : 'archive'} this
                                                    custom field.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => {
                                                        if (field.archived_at) {
                                                            unarchiveField({
                                                                campaignId,
                                                                id: field.id,
                                                            });
                                                        } else {
                                                            archiveField({
                                                                campaignId,
                                                                id: field.id,
                                                            });
                                                        }
                                                    }}
                                                    disabled={isArchiving || isUnArchiving}
                                                >
                                                    Confirm
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                                <AlertDialog.Root type="warning">
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>Delete Field</AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    Deleting this field will also delete all data
                                                    previously collected. To hide this field but
                                                    retain the data, archive the field instead.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() =>
                                                        deleteField({
                                                            campaignId,
                                                            id: field.id,
                                                        })
                                                    }
                                                    disabled={isDeleting}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>
                </div>
                <Collapsible.Content>
                    <CampaignToolsEditCustomField field={field} campaignId={campaignId} />
                </Collapsible.Content>
            </Collapsible.Root>
        </Box>
    );
};

type AccessoryTypeProps = {
    field: CustomField;
};

const BadgeType = ({ field }: AccessoryTypeProps) => {
    if (field.archived_at !== null) {
        return <Badge color="error">Archived</Badge>;
    } else if (field.required !== false) {
        return <Badge color="primary">Required</Badge>;
    }
    return null;
};

const IconType = ({ field }: AccessoryTypeProps) => {
    switch (field.type) {
        case 'text':
            return (
                <img alt="Text Icon" src={`/images/icons/custom-fields/text.svg`} className="h-4" />
            );
        case 'textarea':
            return (
                <img
                    alt="Paragraph Icon"
                    src={`/images/icons/custom-fields/textarea.svg`}
                    className="h-4"
                />
            );
        case 'checkbox':
            return (
                <img
                    alt="Checkbox Icon"
                    src={`/images/icons/custom-fields/checkbox.svg`}
                    className="h-4"
                />
            );
        case 'select':
            return (
                <img
                    alt="Select Icon"
                    src={`/images/icons/custom-fields/select.svg`}
                    className="h-4"
                />
            );
        case 'multiselect':
            return (
                <img
                    alt="Select Icon"
                    src={`/images/icons/custom-fields/multiselect.svg`}
                    className="h-4"
                />
            );
        case 'radio':
            return (
                <img
                    alt="Radio Icon"
                    src={`/images/icons/custom-fields/radio.svg`}
                    className="h-4"
                />
            );
        case 'file':
            return (
                <img alt="File Icon" src={`/images/icons/custom-fields/file.svg`} className="h-4" />
            );
        default:
            return null;
    }
};

export type { NewViewCustomFieldFieldProps };

export { CampaignToolsViewCustomField };
